import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Paper, Grid, TextField, Button, Alert, Divider, Chip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

interface QQConfig {
  smallRoundCount: number;
  smallRoundInterval: number;
  smallRoundPause: number;
  maxSmallRounds: number;
  bigRoundPause: number;
}

const DEFAULT_CONFIG: QQConfig = {
  smallRoundCount: 7,
  smallRoundInterval: 3,
  smallRoundPause: 75,
  maxSmallRounds: 15,
  bigRoundPause: 7200,
};

const QQMusicConfig = () => {
  const [config, setConfig] = useState<QQConfig>(DEFAULT_CONFIG);
  const [form, setForm] = useState<QQConfig>(DEFAULT_CONFIG);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchConfig = async () => {
    const resp = await axios.get('/api/qqmusic/config');
    if (resp.data.success) {
      setConfig(resp.data.config);
      setForm(resp.data.config);
    }
  };

  useEffect(() => { fetchConfig().catch(console.error); }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put('/api/qqmusic/config', form);
      setSaveMsg('配置已保存');
      fetchConfig();
    } catch (e: any) {
      setSaveMsg(e.response?.data?.message || '保存失败');
    } finally {
      setLoading(false);
      setTimeout(() => setSaveMsg(''), 4000);
    }
  };

  return (
    <SidebarLayout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h5">QQ音乐评论发送规则配置</Typography>
        </Box>
        <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#f0fff4' }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5 }}>当前生效配置</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`每小轮 ${config.smallRoundCount} 条`} size="small" variant="outlined" />
            <Chip label={`间隔 ${config.smallRoundInterval} 秒`} size="small" variant="outlined" />
            <Chip label={`小轮暂停 ${config.smallRoundPause} 秒`} size="small" variant="outlined" />
            <Chip label={`最多 ${config.maxSmallRounds} 小轮`} size="small" variant="outlined" />
            <Chip label={`大轮暂停 ${Math.round(config.bigRoundPause / 60)} 分钟`} size="small" variant="outlined" />
          </Box>
        </Paper>
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>修改配置（无最小值限制）</Typography>
          <Divider sx={{ mb: 3 }} />
          {saveMsg && <Alert severity={saveMsg.includes('失败') ? 'error' : 'success'} sx={{ mb: 2 }}>{saveMsg}</Alert>}
          <Grid container spacing={3}>
            {([
              ['smallRoundCount', '每小轮发送条数'],
              ['smallRoundInterval', '小轮内每条间隔（秒）'],
              ['smallRoundPause', '每小轮后暂停（秒）'],
              ['maxSmallRounds', '每大轮最多小轮数'],
              ['bigRoundPause', '大轮后暂停（秒）'],
            ] as const).map(([key, label]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField fullWidth type="number" label={label} value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value, 10) || 0 })} />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={loading}>保存配置</Button>
            <Button variant="outlined" onClick={() => setForm(config)}>重置</Button>
          </Box>
        </Paper>
      </Container>
    </SidebarLayout>
  );
};

export default QQMusicConfig;
